import { Trash2 } from 'lucide-react';
import { HTMLAttributes, HTMLInputTypeAttribute, ReactNode } from 'react';
import * as React from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { cn } from '@/lib/utils';

interface CustomTextFieldProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  addOn?: string;
  disabled?: boolean;
  name: Path<T>;
  label: ReactNode;
  rightLabel?: ReactNode;
  description?: ReactNode;
  autoComplete?: string;
  value?: string;
  index?: number;
  type?: HTMLInputTypeAttribute | undefined;
  remove?: () => void;
  formItemClassName?: HTMLAttributes<HTMLDivElement>['className'];
  /**
   * Hide the per-field error message and instead mark the error by turning the
   * label destructive. For dense forms that already surface errors elsewhere
   * (red border + a step-level marker) so a "required" line under every input
   * doesn't pile on more red. The red border is kept.
   */
  hideErrorMessage?: boolean;
}

type TextFieldProps<T extends FieldValues> = CustomTextFieldProps<T> &
  Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    keyof CustomTextFieldProps<T>
  >;

export function TextField<T extends FieldValues>({
  form,
  addOn,
  disabled,
  name,
  label,
  rightLabel,
  type = 'text',
  description,
  autoComplete,
  value,
  index,
  remove,
  formItemClassName,
  hideErrorMessage,
  ...rest
}: TextFieldProps<T>) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={formItemClassName}>
          {(label || rightLabel) && (
            <FormLabel
              className={cn(
                'font-semibold inline-flex justify-between w-full',
                hideErrorMessage && fieldState.error && 'text-destructive',
              )}
            >
              <div>{label}</div>
              <div>{rightLabel}</div>
            </FormLabel>
          )}

          {description && (
            <FormDescription className="text-sm">{description}</FormDescription>
          )}
          <div className="flex">
            {addOn && (
              <span className="inline-flex items-center rounded-l border border-r-0 border-input px-3 h-10 text-gray-300 sm:text-sm select-none bg-muted/20 font-semibold">
                {addOn}
              </span>
            )}
            <FormControl>
              <Input
                type={type}
                {...field}
                {...rest}
                value={value}
                disabled={disabled}
                autoComplete={autoComplete}
                className={cn(rest.className, {
                  'rounded-l-none': !!addOn,
                })}
              />
            </FormControl>
            {remove && (
              <Button
                className="ml-2"
                type="button"
                size="icon"
                variant="destructive"
                onClick={remove}
                disabled={index !== undefined ? index < 2 : false}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
          {!hideErrorMessage && <FormMessage className="font-semibold" />}
        </FormItem>
      )}
    />
  );
}
